import React from "react";
import { C, fmt, pct, fyl } from "./shared";

const Kpi = ({ label, value, sub, tone, testid }) => (
  <div className="rounded-lg px-4 py-3" data-testid={testid}
    style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
    <div className="text-lg font-extrabold mt-0.5" style={{ color: tone || "#fff" }}>{value}</div>
    {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);

const Spark = ({ data, color, zeroLine }) => {
  const w = 560, h = 90, pad = 4;
  const min = Math.min(0, ...data), max = Math.max(1, ...data);
  const x = (i) => pad + (i / (data.length - 1)) * (w - 2 * pad);
  const y = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - 2 * pad);
  const pts = data.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 90 }}>
      {zeroLine && min < 0 && <line x1={pad} x2={w - pad} y1={y(0)} y2={y(0)} stroke="rgba(248,113,113,0.5)" strokeDasharray="4 3" strokeWidth="1" />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
      <polygon points={`${pad},${y(min < 0 ? min : 0)} ${pts} ${w - pad},${y(min < 0 ? min : 0)}`} fill={color} opacity="0.08" />
    </svg>
  );
};

const LINE_COLORS = ["#7BD3CE", "#F59E0B", "#2CC5A8", "#A78BFA", "#F472B6", "#60A5FA"];

const MultiSpark = ({ series }) => {
  const w = 560, h = 90, pad = 4;
  const all = series.flatMap((s) => s.data);
  const min = Math.min(0, ...all), max = Math.max(1, ...all);
  const n = series[0]?.data.length || 1;
  const x = (i) => pad + (i / (n - 1)) * (w - 2 * pad);
  const y = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - 2 * pad);
  return (
    <div data-testid="fm-dash-revenue-lines">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 90 }}>
        {series.map((s, si) => (
          <polyline key={si} points={s.data.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
            fill="none" stroke={s.color} strokeWidth="2" opacity={si === 0 ? 1 : 0.95} />
        ))}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {series.map((s, si) => (
          <span key={si} className="inline-flex items-center gap-1.5 text-[10px] font-bold text-white/60" data-testid={`fm-dash-line-legend-${si}`}>
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function FmDashboard({ out, setTab, model }) {
  const d = out.dashboard;
  const yl = fyl(out);
  const crs = out.crs;
  const empty = !d.f1_revenue && !d.f5_revenue;
  const trackRows = out.tracking?.months || [];
  const lastRow = trackRows[trackRows.length - 1];
  const needsActuals = !!lastRow && !lastRow.entered;

  // Per-line revenue series — streams (mixed model) + extra revenue lines,
  // each in its own colour so founders see which stream drives growth.
  const revMonths = out.revenue?.months || [];
  const revA = model?.assumptions?.revenue || {};
  const lineSeries = [];
  (revA.streams || []).forEach((s, i) => {
    const data = revMonths.map((m) => m[`stream_${i + 1}`] || 0);
    if (data.some((v) => v > 0)) lineSeries.push({ name: s.name || `Stream ${i + 1}`, data });
  });
  (revA.extra_lines || []).slice(0, 4).forEach((l, i) => {
    const data = revMonths.map((m) => m[`line_${i + 2}`] || 0);
    if (data.some((v) => v > 0)) lineSeries.push({ name: l.name || `Line ${i + 2}`, data });
  });
  if (lineSeries.length > 0 && (revA.streams || []).length === 0) {
    const extrasTotal = (m) => (revA.extra_lines || []).slice(0, 4)
      .reduce((s, _, i) => s + (revMonths[m]?.[`line_${i + 2}`] || 0), 0);
    lineSeries.unshift({
      name: "Main revenue",
      data: revMonths.map((m, i) => Math.max(0, (m.revenue || 0) - extrasTotal(i))),
    });
  }
  const colored = lineSeries.map((s, i) => ({ ...s, color: LINE_COLORS[i % LINE_COLORS.length] }));

  return (
    <div data-testid="fm-dashboard" className="space-y-6">
      {needsActuals && (
        <div className="rounded-xl p-5 flex items-center justify-between gap-4" data-testid="fm-dash-actuals-banner"
          style={{ backgroundColor: "rgba(10,139,133,0.1)", border: "1px solid rgba(10,139,133,0.45)" }}>
          <div>
            <div className="text-white font-extrabold text-sm">How are you tracking against your forecast?</div>
            <div className="text-xs text-white/50 mt-1">
              {lastRow.label} has ended — log what actually happened and Clinq blends it into your model. Funders read monthly variance as management discipline.
            </div>
          </div>
          <button onClick={() => setTab("tracking")} data-testid="fm-dash-goto-tracking"
            className="rounded-md px-4 py-2.5 text-xs font-bold whitespace-nowrap"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            Enter {lastRow.label} actuals →
          </button>
        </div>
      )}
      {empty && (
        <div className="rounded-xl p-6 flex items-center justify-between" style={{ backgroundColor: C.panel, border: "1px solid rgba(245,158,11,0.3)" }}>
          <div>
            <div className="text-white font-extrabold">Nothing to show yet</div>
            <div className="text-xs text-white/45 mt-1">Set your revenue and cost assumptions and this dashboard lights up automatically.</div>
          </div>
          <button onClick={() => setTab("assumptions")} data-testid="fm-dash-goto-assumptions"
            className="rounded-md px-4 py-2.5 text-sm font-bold" style={{ backgroundColor: C.orange, color: "#000" }}>
            Set assumptions →
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi testid="fm-dash-f1rev" label={`${yl[0]} revenue`} value={fmt(d.f1_revenue)} />
        <Kpi testid="fm-dash-f5rev" label={`${yl[4]} revenue`} value={fmt(d.f5_revenue)} sub={d.cagr != null ? `${(d.cagr * 100).toFixed(0)}% CAGR` : null} />
        <Kpi testid="fm-dash-gm" label={`Gross margin (${yl[0]})`} value={pct(d.gross_margin_f1)} />
        <Kpi testid="fm-dash-em" label={`EBITDA margin (${yl[4]})`} value={pct(d.ebitda_margin_f5)} tone={d.ebitda_margin_f5 >= 0 ? C.green : C.red} />
        <Kpi testid="fm-dash-breakeven" label="EBITDA breakeven" value={d.breakeven_label || "Not in plan"} tone={d.breakeven_month ? C.tealSoft : C.orange} />
        <Kpi testid="fm-dash-runway" label="Runway" value={d.first_shortfall_month ? `${d.runway_months} months` : "60+ months"} tone={d.first_shortfall_month ? C.red : C.green}
          sub={d.first_shortfall_month ? `Cash runs out ${d.first_shortfall_label}` : "Cash stays positive"} />
        <Kpi testid="fm-dash-mincash" label="Lowest cash point" value={fmt(d.min_cash)} sub={d.min_cash_label} tone={d.min_cash < 0 ? C.red : undefined} />
        <button onClick={() => setTab("crs")} className="text-left" data-testid="fm-dash-crs">
          <div className="rounded-lg px-4 py-3 h-full" style={{ backgroundColor: C.panel, border: `1px solid ${crs.score >= 80 ? "rgba(44,197,168,0.4)" : "rgba(245,158,11,0.4)"}` }}>
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">CRS readiness</div>
            <div className="text-lg font-extrabold mt-0.5" style={{ color: crs.score >= 80 ? C.green : C.orange }}>{crs.score}/100</div>
            <div className="text-[10px] text-white/40 mt-0.5">{crs.band} · view checks →</div>
          </div>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl p-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">
            Monthly revenue · 60 months{colored.length > 1 ? " · by line" : ""}
          </div>
          {colored.length > 1
            ? <MultiSpark series={colored} />
            : <Spark data={d.series.revenue} color={C.tealSoft} />}
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Closing cash · 60 months</div>
          <Spark data={d.series.closing_cash} color={C.orange} zeroLine />
        </div>
      </div>
      <div className="rounded-xl p-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-3">Annual EBITDA</div>
        <div className="flex items-end gap-3" style={{ height: 90 }}>
          {d.series.annual_ebitda.map((v, i) => {
            const maxA = Math.max(...d.series.annual_ebitda.map(Math.abs), 1);
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <div className="w-full rounded-t" style={{ height: `${Math.max(4, (Math.abs(v) / maxA) * 70)}px`, backgroundColor: v >= 0 ? C.green : C.red, opacity: 0.85 }} />
                <div className="text-[9px] text-white/40 mt-1 font-bold">{yl[i]}</div>
                <div className="text-[9px] font-bold" style={{ color: v >= 0 ? C.green : C.red }}>{fmt(v)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
